// Environmental sensor export module
// Handles Excel and PDF export of the currently displayed temperature/humidity
// (and temp/hum/pressure) sensor data points. Mirrors the Time Tracking export
// flow: Excel via the global XLSX library, PDF via jsPDF using a Filter report
// template chosen in Setup -> Settings (environmental_report_template_id), with
// options for date sort order and whether to include the graphs and/or the data
// point table.

import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { t } from '../core/i18n.js';
import { getCurrentUser } from '../utils/helpers.js';

/**
 * Turn the parallel data arrays into sortable row objects.
 * @param {object} data - { timestamps, temperatures, humidities, pressures, hasPressure }
 * @param {'asc'|'desc'} dateDir
 */
function buildRows(data, dateDir) {
    const rows = data.timestamps.map((ts, i) => ({
        ts,
        temperature: data.temperatures[i],
        humidity: data.humidities[i],
        pressure: data.pressures[i],
    }));
    rows.sort((a, b) => dateDir === 'asc' ? a.ts - b.ts : b.ts - a.ts);
    return rows;
}

/** 24-hour local timestamp, European style. */
function fmtTimestamp(ts) {
    return ts.toLocaleString('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
}

/** Escape HTML special characters for safe injection into the cover markup. */
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Build a safe filename stem from the sensor + range. */
function buildFilenameStem(ctx) {
    const sensor = ctx.sensor || {};
    const namePart = (sensor.name || sensor.sensor_id || 'sensor')
        .replace(/[^a-zA-Z0-9æøåÆØÅ]+/g, '_')
        .replace(/^_|_$/g, '');
    const range = (ctx.data && ctx.data.rangeLabel ? ctx.data.rangeLabel : '')
        .replace(/[^a-zA-Z0-9æøåÆØÅ]+/g, '_')
        .replace(/^_|_$/g, '');
    return `Sensor_${namePart}${range ? '_' + range : ''}`;
}

// ============================================================================
// Excel export
// ============================================================================

export function exportEnvSensorToExcel(ctx) {
    if (!ctx.data || !ctx.data.timestamps || ctx.data.timestamps.length === 0) {
        showError('No data to export');
        return;
    }
    if (typeof XLSX === 'undefined') {
        showError('Excel library not loaded');
        return;
    }

    const { hasPressure } = ctx.data;
    const rows = buildRows(ctx.data, 'asc').map(r => {
        const row = {
            'Timestamp': fmtTimestamp(r.ts),
            'Temperature (°C)': r.temperature != null ? Number(r.temperature.toFixed(2)) : '',
            'Humidity (%)': r.humidity != null ? Number(r.humidity.toFixed(1)) : '',
        };
        if (hasPressure) {
            row['Pressure (hPa)'] = r.pressure != null ? Number(r.pressure.toFixed(1)) : '';
        }
        return row;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto-size columns
    const headers = Object.keys(rows[0]);
    ws['!cols'] = headers.map(header => {
        const maxLength = Math.max(
            header.length,
            ...rows.map(row => String(row[header] ?? '').length)
        );
        return { wch: Math.min(maxLength + 2, 50) };
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Sensor Data');
    XLSX.writeFile(wb, `${buildFilenameStem(ctx)}.xlsx`);
    showSuccess(`Exported ${rows.length} data points`);
}

// ============================================================================
// PDF export
// ============================================================================

/**
 * Show the PDF options modal (sort order + include graphs / table).
 * Resolves with the chosen options, or null if cancelled.
 */
function showEnvPdfOptionsModal() {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.id = 'env-pdf-options-modal';
        modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
        modal.innerHTML = `
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 class="text-lg font-medium text-gray-900">${t('insights.pdfOptionsTitle')}</h3>
                    <button type="button" id="env-pdf-cancel-x" class="text-gray-400 hover:text-gray-500">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="p-6 space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('insights.sortBy')}</label>
                        <select id="env-pdf-sort" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm py-2 px-2">
                            <option value="desc">${t('insights.dateNewestFirst')}</option>
                            <option value="asc">${t('insights.dateOldestFirst')}</option>
                        </select>
                    </div>
                    <label class="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" id="env-pdf-include-graphs" checked
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="text-sm text-gray-700">${t('insights.includeGraphs')}</span>
                    </label>
                    <label class="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" id="env-pdf-include-table" checked
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="text-sm text-gray-700">${t('insights.includeTable')}</span>
                    </label>
                </div>
                <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                    <button type="button" id="env-pdf-cancel"
                            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                        ${t('common.cancel')}
                    </button>
                    <button type="button" id="env-pdf-generate"
                            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                        ${t('insights.generatePdf')}
                    </button>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        const close = (result) => {
            modal.remove();
            resolve(result);
        };
        modal.querySelector('#env-pdf-cancel-x').onclick = () => close(null);
        modal.querySelector('#env-pdf-cancel').onclick = () => close(null);
        modal.querySelector('#env-pdf-generate').onclick = () => {
            close({
                dateDir: modal.querySelector('#env-pdf-sort').value === 'asc' ? 'asc' : 'desc',
                includeGraphs: modal.querySelector('#env-pdf-include-graphs').checked,
                includeTable: modal.querySelector('#env-pdf-include-table').checked,
            });
        };
    });
}

/**
 * Fetch the configured environmental report template (structure + elements),
 * or null if none is configured / it cannot be loaded.
 */
async function loadEnvReportTemplate() {
    let templateId = null;
    try {
        const settings = await apiCall('/settings', { headers: auth.getAuthHeaders() });
        templateId = settings?.environmental_report_template_id || null;
    } catch (e) {
        console.warn('[EnvPDF] Failed to load settings:', e);
    }
    if (!templateId) return null;

    try {
        const template = await apiCall(`/report-templates/templates/${templateId}`, {
            headers: auth.getAuthHeaders(),
        });
        const elements = await apiCall(`/report-templates/templates/${templateId}/elements`, {
            headers: auth.getAuthHeaders(),
        });
        const frontpage = elements
            .filter(e => e.page === 'frontpage')
            .sort((a, b) => (a.position || 0) - (b.position || 0));
        template.frontpageElements = frontpage;
        return template;
    } catch (e) {
        console.warn('[EnvPDF] Failed to load template, continuing without cover:', e);
        return null;
    }
}

/** Render a single template cover element to an HTML string. */
function renderCoverElement(element, ctx) {
    const type = element.element_type || element.type;
    const label = element.intro_text || element.introText || '';
    const data = element.static_data || element.staticData || {};
    const config = element.config || {};
    const labelHtml = label
        ? `<div style="font-size:13px;font-weight:600;color:#374151;margin-bottom:3px;">${escapeHtml(label)}</div>`
        : '';
    switch (type) {
        case 'heading': {
            const text = data.content || label || '';
            const size = config.size || 'medium';
            const px = size === 'large' ? 30 : size === 'small' ? 18 : 24;
            return `<h2 style="font-size:${px}px;font-weight:700;color:#111827;margin:0 0 8px 0;line-height:1.2;">${escapeHtml(text)}</h2>`;
        }
        case 'text-block':
            return `<div style="font-size:14px;color:#374151;white-space:pre-wrap;">${escapeHtml(data.content || '')}</div>`;
        case 'image': {
            const img = data.imageData;
            if (!img) return '';
            const alignment = element.alignment || config.alignment || 'center';
            const margin = alignment === 'left' ? '0' : alignment === 'right' ? '0 0 0 auto' : '0 auto';
            return `<div style="margin-bottom:12px;"><img src="${img}" alt="" style="display:block;max-width:220px;max-height:110px;margin:${margin};" /></div>`;
        }
        case 'divider':
            return `<hr style="border:none;border-top:1px solid #d1d5db;margin:12px 0;" />`;
        case 'filter-date-range':
            return `<div>${labelHtml}<div style="font-size:14px;color:#111827;">${escapeHtml(ctx.rangeLabel || 'N/A')}</div></div>`;
        case 'filter-location':
            return `<div>${labelHtml}<div style="font-size:14px;color:#111827;">${escapeHtml(ctx.locationName || 'N/A')}</div></div>`;
        case 'filter-account':
            return `<div>${labelHtml}<div style="font-size:14px;color:#111827;">${escapeHtml(ctx.accountName || 'N/A')}</div></div>`;
        case 'creator-name':
            return `<div>${labelHtml}<div style="font-size:14px;color:#111827;">${escapeHtml(ctx.creatorName || '')}</div></div>`;
        default:
            return '';
    }
}

/**
 * Build the cover page HTML from the template's frontpage elements, grouping
 * them into the same four-section layout the base front page uses, with fixed
 * proportional heights so each section always occupies its region:
 *   Header ~15% · Main ~40% · Details ~30% · Footer ~15%.
 * Side-by-side zones stay side by side within their section.
 */
function buildCoverHtml(template, ctx) {
    const els = (template && Array.isArray(template.frontpageElements)) ? template.frontpageElements : [];
    const fallback = `<div style="display:flex;align-items:center;justify-content:center;height:100%;">`
        + `<h1 style="font-size:24px;font-weight:700;">${escapeHtml(ctx.title)}</h1></div>`;
    if (els.length === 0) return fallback;

    const zones = {};
    els.forEach(el => {
        const z = el.zone || 'main-content';
        (zones[z] = zones[z] || []).push(el);
    });
    const render = list => (list || []).map(el => renderCoverElement(el, ctx)).join('');
    const hasAny = (...keys) => keys.some(k => zones[k] && zones[k].length);

    const headerBorder = hasAny('header-left', 'header-right') ? 'border-bottom:2px solid #e5e7eb;' : '';
    const footerBorder = hasAny('footer-left', 'footer-right') ? 'border-top:2px solid #e5e7eb;' : '';

    const headerSection = `<div style="height:15%;box-sizing:border-box;display:grid;grid-template-columns:1fr 1fr;gap:24px;align-items:center;padding-bottom:12px;overflow:hidden;${headerBorder}">`
        + `<div>${render(zones['header-left'])}</div>`
        + `<div style="text-align:right;">${render(zones['header-right'])}</div></div>`;

    const mainSection = `<div style="height:40%;box-sizing:border-box;padding:16px 0;overflow:hidden;">`
        + `<div>${render(zones['main-content'])}</div>`
        + `<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:16px;">`
        + `<div>${render(zones['headline-left'])}</div><div>${render(zones['headline-right'])}</div></div></div>`;

    const detailsSection = `<div style="height:30%;box-sizing:border-box;padding:16px 0;display:grid;grid-template-columns:1fr 1fr;gap:16px;align-content:start;overflow:hidden;">`
        + `<div>${render(zones['detail-1'])}</div><div>${render(zones['detail-2'])}</div>`
        + `<div>${render(zones['detail-3'])}</div><div>${render(zones['detail-4'])}</div></div>`;

    const footerSection = `<div style="height:15%;box-sizing:border-box;display:grid;grid-template-columns:1fr 1fr;gap:24px;align-items:center;padding-top:12px;overflow:hidden;${footerBorder}">`
        + `<div>${render(zones['footer-left'])}</div>`
        + `<div style="text-align:right;">${render(zones['footer-right'])}</div></div>`;

    return `<div style="display:flex;flex-direction:column;height:100%;box-sizing:border-box;">`
        + `${headerSection}${mainSection}${detailsSection}${footerSection}</div>`;
}

/** Rasterize a cover HTML string onto a (possibly new) PDF page. */
async function addCoverPage(pdf, html, isFirstPage, pdfWidth, pdfHeight) {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '1000px';
    // Match the PDF page aspect so zones anchored to top/bottom land correctly.
    container.style.height = Math.round(1000 * (pdfHeight / pdfWidth)) + 'px';
    container.style.boxSizing = 'border-box';
    container.style.backgroundColor = '#ffffff';
    container.style.padding = '40px';
    container.innerHTML = html;
    document.body.appendChild(container);
    try {
        await new Promise(r => setTimeout(r, 50));
        const canvas = await html2canvas(container, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        if (!isFirstPage) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pdfHeight));
    } finally {
        container.remove();
    }
}

/** Capture each on-screen chart card and add it to the PDF. */
async function addChartImages(pdf, chartsContainerId, startFresh, pdfWidth, pdfHeight) {
    const container = document.getElementById(chartsContainerId);
    if (!container) return startFresh;
    const cards = container.querySelectorAll('.bg-white.border.rounded-lg');
    const margin = 10;
    // `startFresh` true means the current page is empty and may be drawn on;
    // otherwise (e.g. a cover page is present) charts must begin on a new page.
    let pageHasContent = !startFresh;
    let firstCard = true;
    let y = margin;
    for (const card of cards) {
        let canvas;
        try {
            canvas = await html2canvas(card, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        } catch (e) {
            console.warn('[EnvPDF] Chart capture failed:', e);
            continue;
        }
        const imgWidth = pdfWidth - margin * 2;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const needNewPage = (firstCard && pageHasContent) || (!firstCard && y + imgHeight > pdfHeight - margin);
        if (needNewPage) {
            pdf.addPage();
            y = margin;
        }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', margin, y, imgWidth, imgHeight);
        y += imgHeight + 6;
        firstCard = false;
        pageHasContent = true;
    }
    // Report whether the current page is still empty for the next section.
    return !pageHasContent;
}

/** Add the data-point table using jspdf-autotable. */
function addDataTable(pdf, ctx, dateDir, startFresh) {
    const { hasPressure } = ctx.data;
    const head = [['Timestamp', 'Temp (°C)', 'Humidity (%)']];
    if (hasPressure) head[0].push('Pressure (hPa)');

    const rows = buildRows(ctx.data, dateDir).map(r => {
        const row = [
            fmtTimestamp(r.ts),
            r.temperature != null ? r.temperature.toFixed(2) : '-',
            r.humidity != null ? r.humidity.toFixed(1) : '-',
        ];
        if (hasPressure) row.push(r.pressure != null ? r.pressure.toFixed(1) : '-');
        return row;
    });

    if (typeof pdf.autoTable !== 'function') {
        console.warn('[EnvPDF] autoTable plugin not loaded; skipping table');
        return;
    }
    if (!startFresh) pdf.addPage();
    pdf.autoTable({
        head,
        body: rows,
        startY: 12,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
    });
}

export async function generateEnvSensorPDF(ctx) {
    if (!ctx.data || !ctx.data.timestamps || ctx.data.timestamps.length === 0) {
        showError('No data to export');
        return;
    }
    if (!window.jspdf || typeof html2canvas === 'undefined') {
        showError('PDF libraries not loaded');
        return;
    }

    const options = await showEnvPdfOptionsModal();
    if (!options) return;
    if (!options.includeGraphs && !options.includeTable) {
        showError('Select at least graphs or the data table');
        return;
    }

    // Loading indicator
    const loading = document.createElement('div');
    loading.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
    loading.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
            <svg class="animate-spin h-12 w-12 text-primary-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <p class="text-sm text-gray-600">${t('insights.generatingPdf')}</p>
        </div>
    `;
    document.body.appendChild(loading);

    try {
        const currentUser = getCurrentUser();
        const sensor = ctx.sensor || {};
        const coverContext = {
            title: `${t('insights.sensorReport')} — ${sensor.name || sensor.sensor_id || ''}`,
            rangeLabel: ctx.data.rangeLabel || '',
            locationName: ctx.locationName || '',
            accountName: currentUser?.account_name || '',
            creatorName: currentUser?.full_name || currentUser?.name || currentUser?.email || '',
        };

        const template = await loadEnvReportTemplate();

        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pdfWidth = 297;
        const pdfHeight = 210;

        // 1) Cover page (from template, or a generated title page)
        const coverHtml = buildCoverHtml(template, coverContext);
        await addCoverPage(pdf, coverHtml, true, pdfWidth, pdfHeight);
        let startFresh = false;

        // 2) Graphs
        if (options.includeGraphs) {
            startFresh = await addChartImages(pdf, ctx.chartsContainerId, startFresh, pdfWidth, pdfHeight);
        }

        // 3) Data-point table
        if (options.includeTable) {
            addDataTable(pdf, ctx, options.dateDir, startFresh);
        }

        pdf.save(`${buildFilenameStem(ctx)}.pdf`);
        showSuccess('PDF downloaded successfully');
    } catch (error) {
        console.error('[EnvPDF] Failed to generate PDF:', error);
        showError('Failed to generate PDF');
    } finally {
        loading.remove();
    }
}
